import React from "react";

/**
 * PageTitle — the shared masthead for stage-style pages.
 *
 * One place to rule them all: StagePage AND StoresPage render their title
 * through this component, so any future masthead change made HERE updates
 * every page together (per design rule: no diverging copies).
 *
 * Anatomy (the approved B+C blend):
 *   · lit accent tick — 5px stage-coloured pill with a top sheen + soft
 *     colour glow, anchored to the title line (solid colour in Performance
 *     Mode via the rl-stage-tick kill-list entry)
 *   · title + inline mono count on the same baseline, count in the accent
 *     colour ("Quality Control 14 lines" / "Stores 10 lots in storage")
 *   · `after`    — optional inline extras on the title baseline
 *   · `children` — optional meta row(s) below (filter chips, rework pill…)
 */
export default function PageTitle({ color, title, count, unit = "lines", after = null, children }) {
  return (
    <div className="flex items-start gap-3 min-w-0">
      <div
        className="w-[5px] h-[26px] rounded-full shrink-0 mt-[3px] rl-stage-tick"
        style={{
          backgroundColor: color,
          backgroundImage: "linear-gradient(180deg, rgba(255,255,255,0.35), rgba(255,255,255,0) 60%)",
          boxShadow: `0 1px 2px ${color}59, inset 0 1px 0 rgba(255,255,255,0.40)`,
        }}
      />
      <div className="min-w-0">
        <div className="flex items-baseline gap-2.5">
          <h2 className="text-xl font-bold tracking-tight">{title}</h2>
          <span className="text-sm text-[hsl(var(--muted-foreground))] whitespace-nowrap">
            <span className="font-mono-data text-[15px] font-semibold" style={{ color }}>{count}</span> {unit}
          </span>
          {after}
        </div>
        {children}
      </div>
    </div>
  );
}
